import { useState } from 'react';
import { useProductCostHistory, useProductCostStatistics } from '@/hooks/useCostHistory';
import { TrendingUp, TrendingDown, Minus, Calendar, User, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { formatPrice } from '@/utils/format';
import { Unit, UNIT_LABEL_SHORT, pricePerDisplayFromBase } from '@/utils/units';
import clsx from 'clsx';

interface CostHistoryPanelProps {
  productId: number;
  productName: string;
  /** Unidad mostrada al usuario (kg, g, u). Si no se pasa, se asume 'unit'. */
  displayUnit?: Unit;
}

export default function CostHistoryPanel({ productId, productName: _productName, displayUnit }: CostHistoryPanelProps) {
  void _productName;
  const [isExpanded, setIsExpanded] = useState(true);
  const { data: history, isLoading: loadingHistory } = useProductCostHistory(productId);
  const { data: stats, isLoading: loadingStats } = useProductCostStatistics(productId);

  const unit: Unit = displayUnit || 'unit';
  const unitSuffix = `/${UNIT_LABEL_SHORT[unit]}`;
  const toDisplay = (v: number | string | null | undefined) => {
    if (v == null || v === '') return null;
    const num = typeof v === 'string' ? parseFloat(v) : v;
    if (isNaN(num)) return null;
    return pricePerDisplayFromBase(num, unit);
  };
  const fmt = (v: number | string | null | undefined) => {
    const d = toDisplay(v);
    return d == null ? '-' : formatPrice(d);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getChangeIcon = (percent?: number | string | null) => {
    const num = percent != null ? Number(percent) : null;
    if (!num || num === 0) return <Minus className="h-4 w-4 text-taupe-400" />;
    if (num > 0) return <TrendingUp className="h-4 w-4 text-rose-500" />;
    return <TrendingDown className="h-4 w-4 text-emerald-500" />;
  };

  const getChangeColor = (percent?: number | string | null) => {
    const num = percent != null ? Number(percent) : null;
    if (!num || num === 0) return 'text-taupe-500';
    if (num > 0) return 'text-rose-600';
    return 'text-emerald-600';
  };

  if (loadingHistory || loadingStats) {
    return (
      <div className="bg-white border rounded-lg p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-taupe-200 rounded w-1/3"></div>
          <div className="h-20 bg-taupe-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div className="bg-cream-50 border border-dashed border-cream-300 rounded-lg p-4 text-center text-taupe-500">
        <Package className="h-8 w-8 mx-auto mb-2 text-taupe-400" />
        <p className="text-sm">Sin historial de costos registrado</p>
        <p className="text-xs">El historial comenzará cuando se actualice el costo</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-taupe-200 rounded-lg overflow-hidden">
      {/* Header con estadísticas resumidas */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-cream-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-taupe-700" />
          <span className="font-medium text-taupe-900">Historial de Costos</span>
          <span className="text-sm text-taupe-500">({history.length} registros)</span>
        </div>
        <div className="flex items-center gap-4">
          {stats && stats.total_change_percent != null && (
            <div className={clsx('flex items-center gap-1 text-sm font-medium', getChangeColor(stats.total_change_percent))}>
              {getChangeIcon(stats.total_change_percent)}
              <span>{Number(stats.total_change_percent) > 0 ? '+' : ''}{Number(stats.total_change_percent).toFixed(1)}% total</span>
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-taupe-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-taupe-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-taupe-100">
          {/* Estadísticas */}
          {stats && stats.total_records > 0 && (
            <div className="bg-cream-50 p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-taupe-500 text-xs">Primer costo{unitSuffix}</p>
                <p className="font-medium text-taupe-900">{fmt(stats.first_cost)}</p>
              </div>
              <div>
                <p className="text-taupe-500 text-xs">Último costo{unitSuffix}</p>
                <p className="font-medium text-taupe-900">{fmt(stats.last_cost)}</p>
              </div>
              <div>
                <p className="text-taupe-500 text-xs">Costo mínimo{unitSuffix}</p>
                <p className="font-medium text-emerald-700">{fmt(stats.min_cost)}</p>
              </div>
              <div>
                <p className="text-taupe-500 text-xs">Costo máximo{unitSuffix}</p>
                <p className="font-medium text-rose-700">{fmt(stats.max_cost)}</p>
              </div>
            </div>
          )}

          {/* Lista de cambios */}
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-taupe-50 sticky top-0">
                <tr className="text-taupe-700">
                  <th className="text-left px-4 py-2 font-semibold">Fecha</th>
                  <th className="text-right px-4 py-2 font-semibold">Costo Ant.{unitSuffix}</th>
                  <th className="text-right px-4 py-2 font-semibold">Nuevo Costo{unitSuffix}</th>
                  <th className="text-right px-4 py-2 font-semibold">Cambio</th>
                  <th className="text-left px-4 py-2 font-semibold">Notas</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record) => (
                  <tr key={record.id} className="border-b border-taupe-100 hover:bg-cream-50">
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-taupe-700">
                        <Calendar className="h-3 w-3" />
                        {formatDate(record.created_at)}
                      </div>
                      {record.user_name && (
                        <div className="flex items-center gap-1 text-xs text-taupe-400">
                          <User className="h-3 w-3" />
                          {record.user_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-taupe-500 tabular-nums">
                      {fmt(record.old_cost)}
                    </td>
                    <td className="px-4 py-2 text-right font-medium text-taupe-900 tabular-nums">
                      {fmt(record.new_cost)}
                    </td>
                    <td className={clsx('px-4 py-2 text-right font-medium tabular-nums', getChangeColor(record.cost_change_percent))}>
                      <div className="flex items-center justify-end gap-1">
                        {getChangeIcon(record.cost_change_percent)}
                        {record.cost_change_percent !== null && record.cost_change_percent !== undefined
                          ? `${Number(record.cost_change_percent) > 0 ? '+' : ''}${Number(record.cost_change_percent).toFixed(1)}%`
                          : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-taupe-600 max-w-[260px] truncate">
                      {record.supplier && (
                        <span className="bg-cream-100 text-cream-800 text-xs px-1.5 py-0.5 rounded mr-1">
                          {record.supplier}
                        </span>
                      )}
                      {record.quantity_purchased && (
                        <span className="bg-taupe-100 text-taupe-700 text-xs px-1.5 py-0.5 rounded mr-1">
                          x{record.quantity_purchased}
                        </span>
                      )}
                      {record.notes && (
                        <span title={record.notes}>{record.notes}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
